import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Geolocation } from '@capacitor/geolocation';
import { environment } from '../../../environments/environment';
import { CITIES } from '../../../environments/city.config';

/** A city resolved at runtime (from cities.json) — one isolated backend per city. */
export interface CityRuntime {
  id: string;
  name: string;
  center: [number, number];                 // [lat, lng]
  zoom: number;
  dataSource: 'gtfs' | 'lines';
  apiBaseUrl: string;                        // this city's silo API
  bbox: [number, number, number, number];   // [minLat, minLng, maxLat, maxLng]
}

const STORAGE_KEY = 'syncc.cityId';

/** A fix this recent is returned without waking the GPS chip at all. */
const CACHED_FIX_MAX_AGE_MS = 5 * 60_000;

const LIST_KEY = 'syncc.cities';
const REMOTE_LIST_URL = 'https://syncc.me/cities.json';

/**
 * Runtime city resolution for the single-binary native app:
 *   1) a previously chosen city (persisted), else
 *   2) geolocation → bbox match, else
 *   3) the first city in cities.json (fallback).
 * The active city's `apiBaseUrl` drives every API/socket call (see api.interceptor).
 * Adding a city later is a cities.json publish — no app-store release.
 */
@Injectable({ providedIn: 'root' })
export class CityService {
  private http = inject(HttpClient);

  private readonly _active = signal<CityRuntime>(this.warmStart());
  /** The active city; valid synchronously (defaults until resolve() completes). */
  readonly active = this._active.asReadonly();
  /** True when GPS failed and no saved city exists — app.component shows a picker. */
  readonly needsCityPicker = signal(false);
  /** True only when GPS position matched a supported city bbox. */
  readonly gpsMatched = signal(false);

  private cities: CityRuntime[] = [];

  /** Base URL for the active city's silo API. */
  apiBaseUrl(): string {
    if (environment.apiUrl.startsWith('http://localhost')) return environment.apiUrl;
    return this._active().apiBaseUrl || environment.apiUrl;
  }

  /**
   * Resolve the active city at startup (called from an APP_INITIALIZER).
   * Geolocation is the source of truth and is re-evaluated every launch, so the
   * silo always follows where the user actually is. A previously matched city is
   * kept only as an offline / permission-denied fallback — there is no picker.
   */
  async resolve(): Promise<void> {
    await this.loadCities();

    try {
      // Check permission without triggering a request — the PermissionService owns
      // the native dialog flow (with disclosure). If not yet granted, skip GPS here.
      const permStatus = await Geolocation.checkPermissions();
      if (permStatus.location !== 'granted') throw new Error('permission not granted');

      const pos = await this.withTimeout(
        Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          // Without this every launch forces a brand-new fix from the GPS chip,
          // which costs seconds of splash on each cold start. All this fix has
          // to do is pick a city bbox, and the supported cities are ~230 km
          // apart, so a position from the last few minutes is just as correct
          // and comes back instantly.
          maximumAge: CACHED_FIX_MAX_AGE_MS,
          timeout: 6000,
        }),
        7000,
      );
      const { latitude, longitude } = pos.coords;
      const match = this.cities.find(c => this.inBbox(latitude, longitude, c.bbox));
      if (match) { this._active.set(match); this.save(match.id); this.gpsMatched.set(true); return; }
      // GPS works but user is outside all supported city bboxes.
      // Respect a previously saved city (set by the picker or a prior GPS match) so the
      // picker selection is not undone on every reload.
      const savedIdGps = this.readSaved();
      const savedGps = savedIdGps ? this.cities.find(c => c.id === savedIdGps) : null;
      if (savedGps) { this._active.set(savedGps); return; }
      const buildDefaultGps = this.cities.find(c => c.apiBaseUrl === environment.apiUrl);
      if (buildDefaultGps) { this._active.set(buildDefaultGps); return; }
      if (this.cities[0]) this._active.set(this.cities[0]);
      this.needsCityPicker.set(true);
      return;
    } catch { /* permission denied / timeout → fall back to last known */ }

    // Geolocation unavailable: use the last matched city if known.
    const savedId = this.readSaved();
    const saved = savedId ? this.cities.find(c => c.id === savedId) : null;
    if (saved) {
      this._active.set(saved);
      return;
    }
    // No saved city: match against the build-time API URL so each silo defaults
    // to its own city regardless of the order cities appear in cities.json.
    const buildDefault = this.cities.find(c => c.apiBaseUrl === environment.apiUrl);
    if (buildDefault) { this._active.set(buildDefault); return; }
    // Last resort — ask the user to pick.
    if (this.cities[0]) this._active.set(this.cities[0]);
    this.needsCityPicker.set(true);
  }

  /** Called from the city picker when the user makes a manual selection. */
  selectCity(city: CityRuntime): void {
    this.save(city.id);
    this._active.set(city);
    this.needsCityPicker.set(false);
    // Don't reload - just update the active city and hide the picker
    // The app will use the new city's API URL for subsequent requests
  }

  /** Returns the loaded city list for display in the picker. */
  availableCities(): CityRuntime[] { return this.cities; }

  // ── internals ───────────────────────────────────────────────────────────────

  private async loadCities(): Promise<void> {
    if (environment.apiUrl.startsWith('http://localhost')) {
      // Load bundled cities.json so the picker can show all cities.
      // apiBaseUrl() overrides to localhost regardless of the selection.
      try {
        const r = await firstValueFrom(this.http.get<CityRuntime[]>('assets/cities.json'));
        if (Array.isArray(r) && r.length) { this.cities = r; return; }
      } catch { /* fall through */ }
      this.cities = [this.buildTimeFallback()];
      return;
    }
    // Dev landing (dev.syncc.me) serves prod API URLs in cities.json — always use
    // the bundled file in non-production so set-env.cjs generates the correct dev URLs.
    if (!environment.production) {
      try {
        const r = await firstValueFrom(this.http.get<CityRuntime[]>('assets/cities.json'));
        if (Array.isArray(r) && r.length) { this.cities = r; return; }
      } catch { /* fall through */ }
      this.cities = [this.buildTimeFallback()];
      return;
    }
    // Startup must never wait on the network for this. Awaiting the remote list
    // put a full round trip in front of the splash on every launch, which on a
    // phone connection is most of the wait the driver sees. Use the newest list
    // already on the device — a cached copy of an earlier fetch, else the
    // bundled one — and refresh in the background. Publishing a new cities.json
    // still reaches the app without a release; it lands one launch later.
    const cached = this.readCachedList();
    if (cached) { this.cities = cached; void this.refreshRemoteList(); return; }
    try {
      const r = await firstValueFrom(this.http.get<CityRuntime[]>('assets/cities.json'));
      if (Array.isArray(r) && r.length) { this.cities = r; void this.refreshRemoteList(); return; }
    } catch { /* nothing bundled either — fall through to the network */ }
    try {
      const r = await this.withTimeout(
        firstValueFrom(this.http.get<CityRuntime[]>(REMOTE_LIST_URL)), 4000);
      if (Array.isArray(r) && r.length) { this.cities = r; this.writeCachedList(r); return; }
    } catch { /* offline on a first run */ }
    this.cities = [this.buildTimeFallback()];
  }

  /** Fetches the remote list without blocking anything; used on the next launch. */
  private async refreshRemoteList(): Promise<void> {
    try {
      const r = await firstValueFrom(this.http.get<CityRuntime[]>(REMOTE_LIST_URL));
      if (Array.isArray(r) && r.length) this.writeCachedList(r);
    } catch { /* offline — keep whatever we already have */ }
  }

  private readCachedList(): CityRuntime[] | null {
    try {
      const raw = localStorage.getItem(LIST_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch { return null; }
  }
  private writeCachedList(list: CityRuntime[]): void {
    try { localStorage.setItem(LIST_KEY, JSON.stringify(list)); } catch { /* ignore */ }
  }

  /**
   * The active city, decided synchronously at construction.
   *
   * resolve() no longer runs before the first screen, so by the time it
   * finishes the app may already have issued requests — and apiBaseUrl() drives
   * which city's silo those reach. Both inputs needed here are on the device:
   * the cached city list and the id of the last city resolved. Any launch after
   * the first therefore starts on the correct silo with no await at all, and
   * resolve() only has to correct it if the driver has changed city.
   */
  private warmStart(): CityRuntime {
    const list = this.readCachedList();
    const savedId = this.readSaved();
    const hit = list && savedId ? list.find(c => c.id === savedId) : null;
    return hit ?? this.buildTimeFallback();
  }

  private inBbox(lat: number, lng: number, b: [number, number, number, number]): boolean {
    return lat >= b[0] && lat <= b[2] && lng >= b[1] && lng <= b[3];
  }

  private readSaved(): string | null {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  }
  private save(id: string): void {
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  }

  private withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
    return Promise.race([
      p,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
    ]);
  }

  /** Last-resort default fallback. */
  private buildTimeFallback(): CityRuntime {
    const c = CITIES['cochabamba'];
    return {
      id: c.id, name: c.name, center: c.center, zoom: c.zoom, dataSource: c.dataSource,
      apiBaseUrl: environment.apiUrl, bbox: [-90, -180, 90, 180],
    };
  }
}
